import CropperModal from '@master/Modals/Cropper';
import AssetHelper from '@helpers/Asset';
import $http from '@master/Services/HttpService';

import { clone } from '@helpers/Global';
import { alert } from '@libs/alerts';
import { SIZE } from '@master/constants';

class CropperHelper {
  constructor(route, VueInstance) {
    // file position settings
    this.route = route;
    this.VueInstance = VueInstance;
    this.cropperSettings = {};

    // croptool options
    this.cropperOptions = null;

    this.cropper = null;
    this.cropperInstance = null;
    this.cropperFrame = null;
    this.isVideo = false;

    // when manual is true, auto crop will be ignored
    this.manual = false;
    this.new_media = false;
    this.onsave = null;
    this.oncancel = null;
    this.type = null;
    this.device = null;

    // wheter this cropping cycle did end with auto crop
    this.auto_crop = false;

    // fn which is called before showing cropper, if its not null
    this.beforeShow = null;
  }

  init(options, creative = null) {
    this.cropperOptions = {
      width: '100%',
      height: 480,
      cropSize: {
        width: 100,
        height: 100,
      },
      clip: null,
      grid: 9,
      fixedRatio: true,
      videoButtonsController: null,
      videoTrimmerController: null,
      getVideoTrimmerImage: null,
      globalWindow: null,
      module: null,
      // TODO - move this analytics key to the cropper instance props instead
      asset_name: null,
    };

    for (let key in options) {
      // remove any object reference by cloning the keys
      this.cropperOptions[key] = clone(options[key]);
    }

    if (this.cropperOptions.placement != null) {
      if (
        this.cropperOptions.placement.adsize.height >=
        SIZE.INTERSTITIAL.HEIGHT_HIGH_ASPECT
      ) {
        // high aspect ratio device will have 2 safe areas shown
        this.cropperOptions.safeAreaSize = [
          {
            label: 'Safe area for high aspect ratio',
            top: 120,
            bottom: 120,
          },
          {
            label: 'Safe area for low aspect ratio',
            top: 213,
            bottom: 213,
          },
        ];
      } else if (
        this.cropperOptions.placement.adsize.height >= SIZE.INTERSTITIAL.HEIGHT
      ) {
        this.cropperOptions.safeAreaSize = [
          {
            label: 'Safe area for low aspect ratio',
            top: 143,
            bottom: 143,
          },
        ];
      }
    }

    this.type = null;
    this.device = null;
    if (creative != null) {
      if (creative.type != null) {
        this.type = creative.type;
      }
      if (creative.device != null) {
        this.device = creative.device;
      }
    }
  }

  addOption(key, value) {
    this.cropperOptions[key] = value;
  }

  close(save = false) {
    if (this.cropperFrame != null) {
      this.cropperFrame.parentNode.removeChild(this.cropperFrame);
    }
    this.cropperFrame = null;
    this.cropperInstance = null;
    if (this.cropper != null) {
      this.cropper.unload();
      this.cropper = null;
    }

    if (!save && this.oncancel != null) {
      this.oncancel();
    }
  }

  loadAndWaitForCropperFrame() {
    return new Promise(resolve => {
      this.cropperFrame = document.createElement('iframe');
      this.cropperFrame.style.width = '100%';
      this.cropperFrame.style.height = '100%';
      this.cropperFrame.style.position = 'fixed';
      this.cropperFrame.style.top = 0;
      this.cropperFrame.style.left = 0;
      this.cropperFrame.style.opacity = 0;
      this.cropperFrame.style.zIndex = -2147483647;
      this.cropperFrame.style.border = 0;
      this.cropperFrame.addEventListener('load', () => {
        resolve();
      });
      // add cropper frame to window top, so the options will have global window ready
      window.top.document.body.appendChild(this.cropperFrame);
    });
  }

  async instance(callback) {
    if (window.Cropper == null) {
      /* eslint-disable max-len */
      throw new Error(
        'Cropper library not included, please add `' +
          window.__nexd.cdn +
          'dist/Cropper/cropper.min.js` to your index.\nMake sure Cropper has been loaded before you try to initialize it',
      );
    }

    this.auto_crop = false;

    await this.loadAndWaitForCropperFrame();

    const CropperComponent = this.VueInstance.extend(CropperModal);
    this.cropperInstance = new CropperComponent({
      propsData: {
        cropperFrame: this.cropperFrame,
        isVideo: this.isVideo,
        settings: this.cropperSettings,
        type: this.type,
        device: this.device,
        options: this.cropperOptions,
        route: this.route,
      },
    });
    this.cropperInstance.$mount();

    this.cropperInstance.$on('cropperSave', () => {
      if (this.onsave != null) {
        this.onsave(this.cropperSettings, this.auto_crop);
      }
      this.close(true);
    });

    this.cropperInstance.$on('cropperClose', () => {
      this.close();
    });

    this.cropperInstance.$on('change', settings => {
      this.cropperSettings = settings;
    });

    // add references to video layers
    this.cropperOptions.videoButtonsController =
      this.cropperInstance.$refs.videoButtonsController || null;
    this.cropperOptions.videoTrimmerController =
      this.cropperInstance.$refs.videoTrimmerController || null;

    // set cropper globalwindow as the cropper frame window
    this.cropperOptions.globalWindow = this.cropperFrame.contentWindow;

    // used for DSP app, to give in some specific options
    if (process.env.VUE_APP_cropper_options) {
      // If additional cropping options defined, let's add them
      try {
        this.cropperOptions = Object.assign(
          this.cropperOptions,
          JSON.parse(process.env.VUE_APP_cropper_options),
        );
      } catch (err) {
        throw new Error(err);
      }
    }

    // create cropper library
    this.cropper = new Cropper(
      this.cropperInstance.$refs.cropper,
      this.cropperOptions,
    );

    // append cropper instance to the invisible cropper iframe
    this.cropperFrame.contentDocument.body.appendChild(
      this.cropperInstance.$el,
    );

    // call instance ready to send input to cropper
    callback();

    // when cropper is ready, to the rest
    this.cropper.onCropperReady = () => {
      if (
        this.afterCropperReady != null &&
        typeof this.afterCropperReady === 'function'
      ) {
        this.afterCropperReady(this.cropper);
      }

      // check if initial asset is exact match +- few px, to handle some aspect ratio cases etc
      const allowed_offset = 2;

      let asset_exact_match =
        !this.manual &&
        this.cropperSettings.CropLayer.size.height >=
          this.cropperSettings.SourceLayer.size.height - allowed_offset &&
        this.cropperSettings.CropLayer.size.height <=
          this.cropperSettings.SourceLayer.size.height + allowed_offset &&
        this.cropperSettings.CropLayer.size.width >=
          this.cropperSettings.SourceLayer.size.width - allowed_offset &&
        this.cropperSettings.CropLayer.size.width <=
          this.cropperSettings.SourceLayer.size.width + allowed_offset;

      if (
        (asset_exact_match && this.new_media) ||
        this.cropperOptions.auto_crop
      ) {
        this.auto_crop = true;
        if (this.cropperInstance != null) {
          this.cropperInstance.$emit('cropperSave');
        }
      }

      // load css and show cropper
      const styles_to_load = document.styleSheets.length || 0;
      let loaded_styles = 0;
      for (const indx in document.styleSheets) {
        if (document.styleSheets.hasOwnProperty(indx)) {
          const sheetnode = document.styleSheets[indx]
            ? document.styleSheets[indx].ownerNode
            : null;
          const onload = () => {
            // if cropper closed before onload triggered, return
            if (!this.cropperFrame) return;

            loaded_styles++;
            if (styles_to_load === loaded_styles) {
              // add some transparent background styles for frame body
              this.cropperFrame.contentDocument.body.style.background =
                'transparent';
              this.cropperFrame.contentDocument.documentElement.style.background =
                'transparent';

              // we we dont auto crop and create the instance, trigger ready inside it
              if (this.cropperInstance != null) {
                this.cropperInstance.ready();
              }

              // show the cropper
              this.showCropper();
            }
          };

          // if cropper closed before we begin to load styles
          if (!this.cropperFrame) return;

          this.loadCssStyle(
            this.cropperFrame.contentDocument.head,
            sheetnode,
            onload,
          );
        }
      }
    };
  }

  showCropper() {
    if (this.beforeShow != null && typeof this.beforeShow === 'function') {
      this.beforeShow(
        () => {
          this._show();
        },
        this.cropper,
        () => {
          this.cropperInstance.$emit('cropperSave');
        },
      );
    } else {
      this._show();
    }
  }

  _show() {
    if (this.cropperFrame != null) {
      this.cropperFrame.style.opacity = 1;
      this.cropperFrame.style.display = 'block';
      this.cropperFrame.style.zIndex = 99997; /** same as all modals, look for _variables.scss */
    }
  }

  loadCssStyle(target, cssStyle, cb) {
    if (cssStyle) {
      let cssNode = cssStyle.cloneNode(true);
      if (cssStyle.tagName.toLowerCase() === 'link') {
        cssNode.href = cssStyle.href;
      }
      // check for css file to be fully loaded before initializing cropper
      cssNode.onload = () => {
        if (typeof cb === 'function') cb();
      };
      target.appendChild(cssNode);
    }
  }

  newFromURL(url, lazyload = null) {
    this.cropperSettings = {};
    this.new_media = true;
    this.isVideo = AssetHelper.isVideo(url);
    if (this.isVideo) {
      this.cropperOptions.getVideoTrimmerImage = (el, done) => {
        this.getTrimmerBackgroundImage(url, el, done);
      };
    }

    this.instance(() => {
      this.cropperInstance.init(this.cropper);
      this.cropper.openFromURL(url, null, lazyload);
    });
  }

  editAsset(url, settings = null, lazyload = null) {
    this.new_media = false;
    this.isVideo = AssetHelper.isVideo(url);
    if (this.isVideo) {
      this.cropperOptions.getVideoTrimmerImage = (el, done) => {
        this.getTrimmerBackgroundImage(url, el, done);
      };
    }
    this.cropperSettings = clone(settings);

    this.instance(() => {
      this.cropperInstance.init(this.cropper);
      this.cropper.openFromURL(url, this.cropperSettings, lazyload);
    });
  }

  editModule(module = null, module_options = {}) {
    this.cropperSettings = clone(module);
    this.instance(() => {
      this.cropperInstance.init(this.cropper);
      this.cropper.openModule(this.cropperSettings, module_options);
    });
  }

  // when manual: true - cropper never auto crops
  async inputChangeHandler(e, manual = false) {
    const file = e.target.files[0];
    // if cancel is pressed, file is undefined
    if (file != null) {
      this.isVideo = AssetHelper.isVideo(file.type);
      if (this.isVideo) {
        const duration = await AssetHelper.getVideoDuration(file);
        if (duration < 1) {
          alert(
            undefined,
            'You uploaded an video that is too short (under 1 second). This could create flickering for the ad viewer and we cannot allow that. Please upload a longer video (or use a still image instead).',
            'Uploaded video is too short',
          );
          return false;
        }
      }

      this.cropperSettings = {};
      this.manual = manual;
      this.new_media = true;

      this.instance(() => {
        this.cropperInstance.init(this.cropper);
        this.cropper.openFromFile(file);
      }, true);
    }
    e.target.value = '';
    return true;
  }

  getTrimmerBackgroundImage(url, el, done) {
    const path = 'assets/trimmerbg';
    $http.post(path, { url }, { notification: false }).then(result => {
      el.style.backgroundImage = `url('${result.url}')`;
      el.style.backgroundSize = 'cover';

      // removes loading
      done();
    });
  }

  getFile() {
    if (this.cropper == null) {
      return null;
    }
    return this.cropper.getFile();
  }

  getFileDimensions() {
    let node = {
      width: 0,
      height: 0,
    };

    if (this.cropper != null) {
      node = this.cropper.getImage() || this.cropper.getVideo();
    }

    return {
      width: node.width || node.videoWidth || 0,
      height: node.height || node.videoHeight || 0,
    };
  }

  getBase64Image() {
    let canvas = document.createElement('canvas');
    let ctx = canvas.getContext('2d');
    canvas.width = this.cropperOptions.cropSize.width;
    canvas.height = this.cropperOptions.cropSize.height;

    const image = this.cropper.getImage();
    if (image == null) {
      return null;
    }
    const x =
      this.cropperSettings.SourceLayer.position.x -
      this.cropperSettings.CropLayer.position.x;
    const y =
      this.cropperSettings.SourceLayer.position.y -
      this.cropperSettings.CropLayer.position.y;
    const { width, height } = this.cropperSettings.SourceLayer.size;

    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(image, x, y, width, height);
    return canvas.toDataURL('image/jpeg', 1.0);
  }
}

export default CropperHelper;
